<template>
  <div
    class="relative"
    v-click-outside="closePopup"
  >
    <AppLoader
      v-if="isConnecting || isUserDataLoading"
      color="#FFE78D"
      size="24"
    />
    <button
      v-else-if="user?.id"
      class="trigger"
      aria-label="Wallet menu"
      @click="togglePopup"
    >
      <div class="dot green" />
      <span v-if="breakpoints.mdAndUp" class="short-address">
        {{ cutString(address, 7, 7) }}
      </span>
      <AppIcon :name="connector?.type || connector?.icon" />
      <AppIcon
        v-if="breakpoints.mdAndUp"
        size="6"
        is-fill-color
        :class="[
          'transition-300',
          isPopupVisible ? 'rotate-180' : ''
        ]"
        name="arrow_icon"
      />
    </button>
    <button
      v-else
      class="trigger text-sm"
      aria-label="Connect wallet"
      @click="onUnconnectedWalletClick"
    >
      <div class="dot red" />
      <AppIcon name="wallet_icon" />
      <AppIcon
        v-if="breakpoints.mdAndUp"
        size="6"
        is-fill-color
        :class="[
          'transition-300',
          isPopupVisible ? 'rotate-180' : ''
        ]"
        name="arrow_icon"
      />
    </button>
    <Transition>
      <div v-if="isPopupVisible" class="menu bg-blur">
        <ActiveConnectorInfo v-if="user?.id" @disconnect="disconnect" />
        <WalletConnectors v-else @connect="onConnectorClick"/>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { useAccount, useSignMessage, useConnect, useDisconnect, useReconnect } from '@wagmi/vue'

import UserApi from '@/api/user.js'

const isUserDataLoading = useState(appStateConstants.IS_USER_DATA_LOADING)
const user = useState(appStateConstants.USER)

const breakpoints = useBreakpoints()
const { checkAuthToken } = useAuthApi()
const { setToStorage, removeFromStorage } = useStorage()

const { address, connector, status: accountStatus } = useAccount()
const { connectAsync } = useConnect()
const { reconnectAsync } = useReconnect()
const { signMessageAsync } = useSignMessage()
const { disconnect: disconnectWallet } = useDisconnect()

const isPopupVisible = ref(false)
const isConnecting = computed(() => accountStatus.value === wagmiConnectionStatus.connecting)

async function init() {
  try {
    isUserDataLoading.value = true
    await reconnectAsync()
    if (accountStatus.value === wagmiConnectionStatus.connected) {
      const isAuthTokenValid = checkAuthToken()
      if (isAuthTokenValid) getUser()
      else login()
    } else if (accountStatus.value === wagmiConnectionStatus.disconnected) {
      removeFromStorage(storageKeys.authToken)
      user.value = null
      isUserDataLoading.value = false
    }
  } catch (error) {
    console.log('Reconnect error: ', error)
    isUserDataLoading.value = false
  }
}

function onUnconnectedWalletClick() {
  if (accountStatus.value === wagmiConnectionStatus.connected) {
    isUserDataLoading.value = true
    getUser()
  } else {
    togglePopup()
  }
}

async function onConnectorClick(connector) {
  try {
    if (accountStatus.value === wagmiConnectionStatus.connected) {
      notify('warning', 'Wallet already connected to the app. Please reload the page')
      return
    }
    isUserDataLoading.value = true
    togglePopup()
    await connectAsync({ connector })
    login()
  } catch (error) {
    notify('error', error?.message || 'Connect wallet error')
    console.error('Connect error: ', error)
    isUserDataLoading.value = false
  }
}

async function login() {
  try {
    if (connector.value && !address.value) {
      notify('error', 'This connector doesn\'t work in the selected chain.')
      isUserDataLoading.value = false
      disconnect()
      return
    }

    removeFromStorage(storageKeys.authToken)
    const nonce = await UserApi.getNonce(address.value)
    const message = `Nonce: ${nonce}`

    const signature = await signMessageAsync({ message })
    const response = await UserApi.login({ wallet_address: address.value, signature })

    setToStorage(storageKeys.authToken, response?.token)
    user.value = response?.user || {}
  } catch (error) {
    disconnect()
    console.error('Login user error: ', error)
    notify('error', error?.message || 'Login error')
  } finally {
    isUserDataLoading.value = false
  }
}

async function getUser() {
  try {
    user.value = await UserApi.getMyUser()
  } catch (error) {
    disconnect()
  } finally {
    isUserDataLoading.value = false
  }
}

function disconnect() {
  disconnectWallet()
  removeFromStorage(storageKeys.authToken)
  user.value = null
}

function togglePopup() {
  isPopupVisible.value = !isPopupVisible.value
}

function closePopup() {
  isPopupVisible.value = false
}

watch(accountStatus, (newStatus, oldStatus) => {
  if (
    newStatus == wagmiConnectionStatus.disconnected &&
    oldStatus === wagmiConnectionStatus.connected
  ) {
    removeFromStorage(storageKeys.authToken)
      user.value = null
      isUserDataLoading.value = false
  }
})

watch(address, (newAdress, oldAddress) => {
  if (newAdress && oldAddress) {
    login()
  }
})

init()
</script>

<style lang="scss" scoped>
.trigger {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: transparent;
  border: none;
  outline: none;
  cursor: pointer;
  user-select: none;
  gap: 8px;

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;

    &.red {
      background-color: $red;
    }

    &.green {
      background-color: $green;
    }
  }

  .short-address {
    font-size: 16px;
    line-height: 24px;
    color: $white;
  }
}

.menu {
  right: 0;
  padding: 32px;
  border-radius: 12px;
  position: absolute;
  top: calc(100% + 32px);
  background-color: rgba($color: $black, $alpha: .64);
}

@media only screen and (max-width: $sm) {
  .menu {
    right: -20px;
    width: calc(100vw - 32px);
  }
}
</style>
